import { X } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export type SheetProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  description?: string;
  children: ReactNode;
};

// Sheet/Drawer responsivo:
//  - Mobile (<768px): full-screen sobre toda a viewport, BRANCO SOLIDO
//    (sem opacidade), sem backdrop visivel (drawer cobre tudo).
//  - Desktop (>=768px): drawer lateral direito 480px, backdrop preto
//    com 40% de opacidade clicavel para fechar.
//  - Sempre: bg-white solido (sem bg-white/90 nem blur que faria o
//    conteudo parecer translucido), header sticky no topo com X,
//    safe-area-inset respeitado em iOS.
export const Sheet = ({ open, onClose, title, description, children }: SheetProps) => {
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    const originalOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = originalOverflow;
    };
  }, [open, onClose]);

  if (!open) return null;

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? 'sheet-title' : undefined}
      className="fixed inset-0 z-50"
    >
      {/* Backdrop apenas em desktop (mobile = drawer cobre 100%). */}
      <button
        type="button"
        aria-label="Fechar"
        onClick={onClose}
        className="absolute inset-0 hidden cursor-default bg-black/40 md:block"
      />

      {/* Drawer: mobile inset-0 full-screen, desktop lateral 480px. */}
      <div className="absolute inset-0 flex flex-col bg-white md:inset-y-0 md:left-auto md:right-0 md:w-[480px] md:border-l md:border-gray-100 md:shadow-2xl">
        <header
          className="sticky top-0 z-10 flex items-start justify-between gap-3 border-b border-gray-100 bg-white px-5 py-4"
          style={{ paddingTop: 'max(16px, env(safe-area-inset-top))' }}
        >
          <div className="min-w-0">
            {title ? (
              <h2
                id="sheet-title"
                className="truncate text-base font-semibold tracking-tight text-gray-900"
              >
                {title}
              </h2>
            ) : null}
            {description ? (
              <p className="mt-0.5 truncate text-xs text-gray-500">{description}</p>
            ) : null}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-gray-700 transition-colors hover:bg-gray-100 active:bg-gray-200"
          >
            <X className="h-5 w-5" />
          </button>
        </header>

        <div
          className="flex-1 overflow-y-auto bg-white px-5 py-5"
          style={{ paddingBottom: 'max(20px, env(safe-area-inset-bottom))' }}
        >
          {children}
        </div>
      </div>
    </div>,
    document.body,
  );
};
